import React, { useMemo } from 'react';
import {
  Container,
  DownloadContainer,
  DownloadText,
  AdditionalServiceTitle,
  AdditionalServiceText,
  AdditionalServiceOptions,
  AdditionalServiceOptionsContainer,
  AdditionalName,
  AdditionalValue,
  ValueInfoContainer,
  SelectServicesButton,
  SelectServicesButtonWrapper,
  KnowMoreContainer,
  StatusMessageContainer,
  StatusMessage,
  AdditionalServiceCard,
  ECVCardContainer,
  ECVTitle,
  ExternalServiceName,
  ServiceIcon,
  ExpiredText,
} from './styles';
import { AdditionalServicesModal } from './AdditionalServicesModal.jsx';
import { useAdditionalServicesContext } from './AdditionalServicesContext';
import { ServiceCardSkeleton } from './components';
import ECVCardIcon from '../../../../images/icons/ecv-download.svg';
import WaitIcon from '../../../../images/icons/wait-service.svg';
import DownloadIcon from '../../../../images/icons/ecv-download-icon.svg';
import { useSegment } from '../../../../hooks/useSegment';
import { formatPrice } from '../../../../utils/formatPrice';

export const AdditionalServices = ({
  inspectionId,
  winningBid,
  year,
  make,
  model,
  plateNumber,
  isPartnerEvaluated,
}) => {
  const { trackAdditionalService } = useSegment();
  const {
    servicesWithStatus,
    selectedServices,
    setModalOpen,
    handleServiceSelection,
    hasAvailableServices,
    docStatusLoading,
    docStatus,
  } = useAdditionalServicesContext();
  console.log(`🔍 servicesWithStatus >>>>>`, servicesWithStatus);

  const handleSegment = (type, pdfLink) => {
    trackAdditionalService(
      new Date().getTime(),
      inspectionId,
      type,
      year,
      make,
      model,
      winningBid,
      pdfLink,
      plateNumber
    );
  };

  const AdditionalServicesItems = useMemo(() => {
    // Sempre mostra serviços com status 'ready', 'waiting' ou 'expired' (já solicitados)
    const requestedServices = servicesWithStatus.filter(
      service =>
        (service.status === 'ready' || service.status === 'waiting' || service.status === 'expired') &&
        service.showCard
    );

    // Se há serviços já solicitados, mostra apenas eles
    if (requestedServices.length > 0) {
      return requestedServices;
    }

    // Caso contrário, mostra todos os serviços disponíveis para seleção
    const availableServices = servicesWithStatus.filter(service => service.showCard);
    return availableServices;
  }, [servicesWithStatus, isPartnerEvaluated, inspectionId]);

  const AdditionalServicesModalItems = useMemo(() => {
    return servicesWithStatus;
  }, [servicesWithStatus, isPartnerEvaluated, inspectionId]);

  return !isPartnerEvaluated ? (
    <>
      {/* Mostrar seção de anexos apenas se o ECV estiver pronto para download */}
      {docStatus?.ecv?.status === 'ready' && docStatus?.ecv?.url && (
        <Container style={{ marginBottom: '12px' }}>
          <AdditionalServiceText>
            <AdditionalServiceTitle size="1rem">Anexos</AdditionalServiceTitle>
            <ECVCardContainer>
              <img src={ECVCardIcon} alt="ECV" />
              <ECVTitle>ECV</ECVTitle>
              <DownloadContainer
                target="_blank"
                href={docStatus?.ecv?.url}
                onClick={e => {
                  handleSegment('ECV', docStatus?.ecv?.url);
                }}
              >
                <img className="icon" src={DownloadIcon} alt="ícone" style={{ marginRight: 10 }} />
                <DownloadText>Baixar</DownloadText>
              </DownloadContainer>
            </ECVCardContainer>
          </AdditionalServiceText>
        </Container>
      )}
      <Container>
        <AdditionalServiceText>
          <AdditionalServiceTitle size="1.5rem">Serviços adicionais</AdditionalServiceTitle>
        </AdditionalServiceText>

        {servicesWithStatus.some(
          service =>
            service.status &&
            (service.status === 'waiting' || service.status === 'ready' || service.status === 'expired')
        ) && (
          <AdditionalServiceText mb="19px" mt="10px">
            <AdditionalServiceTitle size="1rem">Serviços já solicitados:</AdditionalServiceTitle>
          </AdditionalServiceText>
        )}

        <AdditionalServiceOptionsContainer>
          {docStatusLoading ? (
            // Mostra skeleton durante o carregamento inicial
            <>
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
            </>
          ) : (
            // Mostra os cards reais após o carregamento
            AdditionalServicesItems.map(service => (
              <AdditionalServiceCard
                key={service.key}
                data-service-card
                isOffer={service.status === 'offer'}
              >
                {service.status === 'waiting' && (
                  <StatusMessageContainer status="waiting">
                    <img
                      className="icon"
                      src={WaitIcon}
                      alt="ícone"
                      style={{ marginRight: 6.67 }}
                    />
                    <StatusMessage status="waiting">Aguardando</StatusMessage>
                  </StatusMessageContainer>
                )}
                {service.status === 'ready' && (
                  <StatusMessageContainer status="ready">
                    <StatusMessage status="ready">
                      {service.key === '02' ? 'Garantia contratada!' : 'Pronto!'}
                    </StatusMessage>
                  </StatusMessageContainer>
                )}
                {service.status === 'expired' && (
                  <StatusMessageContainer status="expired">
                    <StatusMessage status="expired">Pronto!</StatusMessage>
                  </StatusMessageContainer>
                )}
                <AdditionalServiceOptions
                  status={service.status}
                  isActive={selectedServices.includes(service.key)}
                  isOffer={service.status === 'offer'}
                  onClick={() => {
                    handleServiceSelection(service.key);
                  }}
                >
                  <ServiceIcon
                    className="icon"
                    src={service.icon}
                    alt="ícone"
                    isOffer={service.status === 'offer'}
                  />
                  <ValueInfoContainer isOffer={service.status === 'offer'}>
                    <div>
                      <AdditionalName>{service.title}</AdditionalName>
                      <AdditionalValue>R$ {formatPrice(service.value)}</AdditionalValue>
                    </div>
                    {(service?.key === '01' || service?.key === '02') && (
                      <KnowMoreContainer
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = service.key === '01' 
                            ? 'https://venda.instacarro.com/laudo-venda/lp'
                            : 'https://venda.instacarro.com/garantia';
                          window.open(url, '_blank');
                        }}
                      >
                        Saiba mais
                      </KnowMoreContainer>
                    )}
                  </ValueInfoContainer>
                  {service?.status === 'ready' &&
                    service?.status !== 'expired' &&
                    service?.key !== '02' && (
                      <div>
                        <DownloadContainer
                          href={service.link}
                          target="_blank"
                          onClick={e => {
                            // Permite o download e faz tracking
                            handleSegment(service.title, service.link);
                          }}
                        >
                          <img
                            className="icon"
                            src={DownloadIcon}
                            alt="ícone"
                            style={{ marginRight: 10 }}
                          />
                          <DownloadText>Baixar</DownloadText>
                        </DownloadContainer>
                      </div>
                    )}

                  {service?.status === 'expired' && service?.key !== '02' && (
                    <div>
                      <DownloadContainer>
                        <ExpiredText>Expirado</ExpiredText>
                      </DownloadContainer>
                    </div>
                  )}

                  {service.status === 'offer' && (
                    <ExternalServiceName isOffer={service.status === 'offer'}>
                      {service.title}
                    </ExternalServiceName>
                  )}
                </AdditionalServiceOptions>
              </AdditionalServiceCard>
            ))
          )}
        </AdditionalServiceOptionsContainer>

        <SelectServicesButtonWrapper>
          {docStatusLoading ? (
            // Mostra skeleton do botão durante o carregamento
            <SelectServicesButton disabled>Carregando...</SelectServicesButton>
          ) : servicesWithStatus.some(
              service =>
                service.status &&
                (service.status === 'waiting' ||
                  service.status === 'ready' ||
                  service.status === 'expired')
            ) ? (
            <SelectServicesButton
              disabled={!hasAvailableServices}
              onClick={() => {
                if (!hasAvailableServices) return;
                setModalOpen(true);
              }}
            >
              Solicitar outros serviços
            </SelectServicesButton>
          ) : (
            <SelectServicesButton
              disabled={!hasAvailableServices || selectedServices.length === 0}
              onClick={() => {
                if (!hasAvailableServices || selectedServices.length === 0) return;
                setModalOpen(true);
              }}
            >
              Solicitar Serviços
            </SelectServicesButton>
          )}
        </SelectServicesButtonWrapper>
      </Container>

      <AdditionalServicesModal services={AdditionalServicesModalItems} />
    </>
  ) : null;
};
